import { memo } from 'react';
import { AlertCircle, Info, Loader2 } from 'lucide-react';
import { StatusMessage } from '@aimsos/ui';

/**
 * @param {{
 *   t: (key: string) => string,
 *   instruction: string,
 *   onInstructionChange: (value: string) => void,
 *   disabled: boolean,
 *   loading: boolean,
 *   fetchError: string | null,
 * }} props
 */
const InstructionsTabPanel = memo(
  ({ t, instruction, onInstructionChange, disabled, loading, fetchError, showEmptyNotice = false }) => {
    if (disabled) {
      return (
        <StatusMessage
          icon={Info}
          title={t('edit.instructions.needAgentTitle')}
          subtitle={t('edit.instructions.needAgentSubtitle')}
        />
      );
    }

    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-content-muted">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-sm">{t('edit.instructions.loading')}</p>
        </div>
      );
    }

    if (fetchError) {
      return (
        <StatusMessage
          icon={AlertCircle}
          iconClassName="text-red-500"
          title={t('edit.instructions.loadError')}
          subtitle={fetchError}
        />
      );
    }

    return (
      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-base font-semibold text-content">{t('edit.instructions.builderTitle')}</h2>
          <p className="mt-1 text-sm text-content-muted">{t('edit.instructions.builderSubtitle')}</p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-content-secondary" htmlFor="agents-instruction-text">
            {t('edit.instructions.writeLabel')}
          </label>
          <div className="relative min-h-[200px] rounded-lg border border-edge-strong bg-surface px-3 py-2.5 focus-within:ring-2 focus-within:ring-[#2173FF]">
            <textarea
              id="agents-instruction-text"
              value={instruction ?? ''}
              onChange={(e) => onInstructionChange(e.target.value)}
              placeholder={t('edit.instructions.placeholder')}
              rows={12}
              className="min-h-[180px] w-full resize-y bg-transparent text-sm text-content placeholder:text-content-faint focus:outline-none"
              aria-describedby={showEmptyNotice ? 'agents-instruction-empty-notice' : undefined}
            />
          </div>
          {showEmptyNotice ? (
            <p
              id="agents-instruction-empty-notice"
              role="status"
              className="flex items-start gap-1.5 text-xs text-content-muted"
            >
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              {t('edit.instructions.emptyNotice')}
            </p>
          ) : null}
        </div>
      </div>
    );
  },
);

InstructionsTabPanel.displayName = 'InstructionsTabPanel';

export default InstructionsTabPanel;
